import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { DataTable, DataTableColumn } from '../../components/ui/data-table';
import PageHeader from '../../components/common/PageHeader';
import RiskScoreBadge from '../../components/trading/RiskScoreBadge';
import CopySettingsDialog, {
  CopySettings,
} from '../../components/trading/CopySettingsDialog';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PerformanceChart from '../../components/trading/PerformanceChart';
import { ArrowLeft, Star } from 'lucide-react';
import toast from 'react-hot-toast';

const defaultSettings: CopySettings = {
  copyAmount: '100',
  allocationType: 'proportional',
  fixedLot: '0.10',
  riskMultiplier: '1.0',
  maxDrawdown: '20',
  maxDailyLoss: '100',
  maxOpenTrades: '5',
  copyOpenTrades: false,
  reverseCopy: false,
  slippagePips: '3',
  stopCopyLevel: '',
};

type OpenTrade = {
  id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  lots: number;
  openPrice: number;
  currentPrice: number;
  profit: number;
  openTime: string;
};

type ClosedTrade = {
  id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  lots: number;
  openPrice: number;
  closePrice: number;
  profit: number;
  closeTime: string;
};

type Follower = {
  id: string;
  name: string;
  copiedAmount: number;
  profit: number;
  joinDate: string;
};

type Review = {
  id: string;
  author: string;
  rating: number;
  date: string;
  comment: string;
};

const StrategyDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [copySettings, setCopySettings] =
    useState<CopySettings>(defaultSettings);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const strategy = {
    id: id || 'TRD001',
    name: 'John Smith',
    strategyName: 'Gold Scalper Pro',
    description:
      'High-frequency gold trading strategy with tight risk control and automated algorithmic execution focused on intraday trends.',
    roi: 42.5,
    winRate: 78.4,
    totalProfit: 56875,
    aum: 125000,
    followers: 45,
    riskLevel: 'high' as const,
    riskScore: 8,
    profitFactor: 2.15,
    avgTrade: 145.2,
    maxDrawdown: 12.4,
    minCopyAmount: 100,
    commissionRate: 15,
    status: 'active',
  };

  const openTrades: OpenTrade[] = [
    {
      id: 'OT001',
      symbol: 'XAUUSD',
      type: 'BUY',
      lots: 0.5,
      openPrice: 2032.5,
      currentPrice: 2038.1,
      profit: 280.0,
      openTime: '2024-02-05 10:15',
    },
    {
      id: 'OT002',
      symbol: 'EURUSD',
      type: 'SELL',
      lots: 1.0,
      openPrice: 1.0785,
      currentPrice: 1.0762,
      profit: 230.0,
      openTime: '2024-02-05 11:30',
    },
  ];

  const closedTrades: ClosedTrade[] = [
    {
      id: 'CT001',
      symbol: 'GBPUSD',
      type: 'BUY',
      lots: 0.8,
      openPrice: 1.261,
      closePrice: 1.2655,
      profit: 360.0,
      closeTime: '2024-02-04 16:45',
    },
    {
      id: 'CT002',
      symbol: 'USDJPY',
      type: 'SELL',
      lots: 1.2,
      openPrice: 148.5,
      closePrice: 148.1,
      profit: 320.0,
      closeTime: '2024-02-04 14:20',
    },
  ];

  const followersList: Follower[] = [
    {
      id: 'FL01',
      name: 'Alex M.',
      copiedAmount: 5000,
      profit: 750,
      joinDate: '2024-01-10',
    },
    {
      id: 'FL02',
      name: 'Sarah K.',
      copiedAmount: 12000,
      profit: 1840,
      joinDate: '2024-01-15',
    },
    {
      id: 'FL03',
      name: 'David R.',
      copiedAmount: 3500,
      profit: 420,
      joinDate: '2024-01-22',
    },
  ];

  const reviewsList: Review[] = [
    {
      id: 'RV01',
      author: 'Mark T.',
      rating: 5,
      date: '2024-01-28',
      comment:
        'Excellent strategy! Steady gains and minimal drawdown during news events.',
    },
    {
      id: 'RV02',
      author: 'Elena P.',
      rating: 5,
      date: '2024-01-20',
      comment:
        'Very consistent trader. Transparent updates and tight risk management.',
    },
  ];

  const openTradeColumns: DataTableColumn<OpenTrade>[] = [
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: r => <span className="font-semibold">{r.symbol}</span>,
      sortable: true,
    },
    {
      id: 'type',
      header: 'Type',
      accessor: r => (
        <Badge
          className={
            r.type === 'BUY'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }
        >
          {r.type}
        </Badge>
      ),
      sortable: true,
    },
    { id: 'lots', header: 'Lots', accessor: r => r.lots, sortable: true },
    {
      id: 'openPrice',
      header: 'Entry',
      accessor: r => r.openPrice,
      sortable: true,
    },
    {
      id: 'currentPrice',
      header: 'Current',
      accessor: r => r.currentPrice,
      sortable: true,
    },
    {
      id: 'profit',
      header: 'Floating P/L',
      accessor: r => (
        <span
          className={
            r.profit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${r.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
    },
    {
      id: 'openTime',
      header: 'Open Time',
      accessor: r => r.openTime,
      sortable: true,
    },
  ];

  const closedTradeColumns: DataTableColumn<ClosedTrade>[] = [
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: r => <span className="font-semibold">{r.symbol}</span>,
      sortable: true,
    },
    {
      id: 'type',
      header: 'Type',
      accessor: r => (
        <Badge
          className={
            r.type === 'BUY'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }
        >
          {r.type}
        </Badge>
      ),
      sortable: true,
    },
    { id: 'lots', header: 'Lots', accessor: r => r.lots, sortable: true },
    {
      id: 'openPrice',
      header: 'Open Price',
      accessor: r => r.openPrice,
      sortable: true,
    },
    {
      id: 'closePrice',
      header: 'Close Price',
      accessor: r => r.closePrice,
      sortable: true,
    },
    {
      id: 'profit',
      header: 'Realized Profit',
      accessor: r => (
        <span
          className={
            r.profit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${r.profit.toFixed(2)}
        </span>
      ),
      sortable: true,
    },
    {
      id: 'closeTime',
      header: 'Close Time',
      accessor: r => r.closeTime,
      sortable: true,
    },
  ];

  const handleStartCopying = async () => {
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 800));
    setIsSubmitting(false);
    setShowConfirmDialog(false);
    toast.success(`Successfully subscribed to ${strategy.strategyName}`);
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() =>
              navigate('/trading-services/copy-trading/marketplace')
            }
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <PageHeader
            title={strategy.strategyName}
            description={`Master Trader: ${strategy.name}`}
          />
        </div>
        <Button onClick={() => setShowCopyDialog(true)}>Copy Strategy</Button>
      </div>

      {/* Overview Cards Bar */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs text-muted-foreground">ROI</span>
          <span className="text-2xl font-bold text-green-600">
            +{strategy.roi}%
          </span>
        </Card>
        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs text-muted-foreground">Total Profit</span>
          <span className="text-2xl font-bold text-green-600">
            ${strategy.totalProfit.toLocaleString()}
          </span>
        </Card>
        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs text-muted-foreground">Win Rate</span>
          <span className="text-2xl font-bold text-blue-600">
            {strategy.winRate}%
          </span>
        </Card>
        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs text-muted-foreground">Profit Factor</span>
          <span className="text-2xl font-bold text-purple-600">
            {strategy.profitFactor}
          </span>
        </Card>
        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs text-muted-foreground">Max Drawdown</span>
          <span className="text-2xl font-bold text-red-600">
            {strategy.maxDrawdown}%
          </span>
        </Card>
        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs text-muted-foreground">Risk Score</span>
          <div className="pt-1">
            <RiskScoreBadge score={strategy.riskScore} />
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid grid-cols-7 w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="open-trades">Open Trades</TabsTrigger>
          <TabsTrigger value="trade-history">Trade History</TabsTrigger>
          <TabsTrigger value="monthly">Monthly</TabsTrigger>
          <TabsTrigger value="followers">Followers</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Strategy Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>{strategy.description}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t pt-4 text-foreground">
                <div>
                  <span className="text-xs text-muted-foreground block">
                    AUM
                  </span>
                  <span className="font-semibold text-lg">
                    ${strategy.aum.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Followers
                  </span>
                  <span className="font-semibold text-lg">
                    {strategy.followers}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Min. Investment
                  </span>
                  <span className="font-semibold text-lg">
                    ${strategy.minCopyAmount}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">
                    Performance Fee
                  </span>
                  <span className="font-semibold text-lg">
                    {strategy.commissionRate}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Equity & Growth Curve</CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <PerformanceChart
                data={[
                  { date: 'Jan', value: 10000 },
                  { date: 'Feb', value: 11200 },
                  { date: 'Mar', value: 12500 },
                  { date: 'Apr', value: 13800 },
                  { date: 'May', value: 14250 },
                ]}
                valueLabel="Equity ($)"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Equity Curve</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <PerformanceChart
                  data={[
                    { date: 'Jan', value: 10000 },
                    { date: 'Feb', value: 11200 },
                    { date: 'Mar', value: 12500 },
                    { date: 'Apr', value: 13800 },
                    { date: 'May', value: 14250 },
                  ]}
                  valueLabel="Equity ($)"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Balance Curve</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <PerformanceChart
                  data={[
                    { date: 'Jan', value: 10000 },
                    { date: 'Feb', value: 11000 },
                    { date: 'Mar', value: 12200 },
                    { date: 'Apr', value: 13500 },
                    { date: 'May', value: 14000 },
                  ]}
                  valueLabel="Balance ($)"
                />
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Return (%)</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <PerformanceChart
                  data={[
                    { date: 'Jan', value: 5.2 },
                    { date: 'Feb', value: 6.8 },
                    { date: 'Mar', value: 7.1 },
                    { date: 'Apr', value: 8.5 },
                    { date: 'May', value: 4.2 },
                  ]}
                  valueLabel="Return (%)"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Drawdown Curve (%)</CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <PerformanceChart
                  data={[
                    { date: 'Jan', value: -1.2 },
                    { date: 'Feb', value: -2.4 },
                    { date: 'Mar', value: -1.8 },
                    { date: 'Apr', value: -3.5 },
                    { date: 'May', value: -2.1 },
                  ]}
                  valueLabel="Drawdown (%)"
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="open-trades">
          <Card>
            <CardHeader>
              <CardTitle>Active Open Master Positions</CardTitle>
              <CardDescription>
                Live positions currently open by {strategy.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={openTradeColumns}
                data={openTrades}
                showAddButton={false}
                showExportButton={false}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trade-history">
          <Card>
            <CardHeader>
              <CardTitle>Historical Executed Trades</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={closedTradeColumns}
                data={closedTrades}
                showAddButton={false}
                showExportButton={false}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monthly">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Return Breakdown (%)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {[
                  'Jan',
                  'Feb',
                  'Mar',
                  'Apr',
                  'May',
                  'Jun',
                  'Jul',
                  'Aug',
                  'Sep',
                  'Oct',
                  'Nov',
                  'Dec',
                ].map((m, idx) => (
                  <div
                    key={m}
                    className="p-3 border rounded-md text-center bg-muted/20"
                  >
                    <span className="text-xs text-muted-foreground block">
                      {m}
                    </span>
                    <span className="font-semibold text-green-600">
                      +{(idx * 1.8 + 2.5).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="followers">
          <Card>
            <CardHeader>
              <CardTitle>
                Active Copying Investors ({followersList.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {followersList.map(fl => (
                <div
                  key={fl.id}
                  className="flex items-center justify-between p-3 border rounded-md"
                >
                  <div>
                    <span className="font-medium text-sm block">{fl.name}</span>
                    <span className="text-xs text-muted-foreground">
                      Joined {fl.joinDate}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold text-sm block">
                      ${fl.copiedAmount.toLocaleString()} Copied
                    </span>
                    <span className="text-xs text-green-600 font-semibold">
                      +${fl.profit} Profit
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews">
          <Card>
            <CardHeader>
              <CardTitle>Investor Reviews & Community Ratings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {reviewsList.map(rv => (
                <div key={rv.id} className="p-4 border rounded-md space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">{rv.author}</span>
                    <div className="flex items-center text-amber-500">
                      {[...Array(rv.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-current" />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{rv.comment}</p>
                  <span className="text-[10px] text-muted-foreground block">
                    {rv.date}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Subscribe Dialog */}
      <CopySettingsDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        traderName={strategy.name}
        strategyName={strategy.strategyName}
        riskScore={strategy.riskScore}
        minCopyAmount={strategy.minCopyAmount}
        commissionRate={strategy.commissionRate}
        settings={copySettings}
        onSettingsChange={setCopySettings}
        onConfirm={() => {
          setShowCopyDialog(false);
          setShowConfirmDialog(true);
        }}
      />

      <ConfirmationModal
        isOpen={showConfirmDialog}
        title="Confirm Copy Trading Subscription"
        message={`Start copying ${strategy.strategyName} by ${strategy.name} with $${copySettings.copyAmount}?`}
        confirmText="Confirm & Start Copying"
        onConfirm={handleStartCopying}
        onCancel={() => setShowConfirmDialog(false)}
        type="info"
        loading={isSubmitting}
      />
    </div>
  );
};

export default StrategyDetails;
